import { Component, ChangeDetectionStrategy, OnInit, Input, Output, EventEmitter, ViewEncapsulation } from '@angular/core';
import { AssistedBrowsingService } from '../assisted-browsing.service';
import { NavigateAction } from './navigation-controls/navigate-action';
import { ModalService } from 'shared/components/modal-dialog/modal-dialog.service';

@Component({
  selector: 'navigation-bar',
  templateUrl: './navigation-bar.component.html',
  styleUrls: ['./navigation-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class NavigationBarComponent implements OnInit {
  @Input()
  public visitorTabs: { title: string, url: string, pageViewId: string }[];

  @Input()
  public currentUrl: string;

  @Output()
  public selectionChange = new EventEmitter<any>();

  public bookmarks: { name: string, url: string }[] = [];
  public isModalOpen: boolean = false;
  public showMoreTabs: boolean = false;
  public showMoreBookmarks: boolean = false;
  public visibleTabCount = 5;
  public visibleBookmarkCount = 5;

  constructor(private _service: AssistedBrowsingService,
              private _modalService: ModalService) {
  }

  async ngOnInit() {
    this.bookmarks = await this._service.getBookmarks();
  }

  onUrlInputEnter(value: string) {
    if (value) {
      this._triggerUrlChangeEventAfterConfirmation({ param: value, action: NavigateAction.PUSH_PAGE });
    }
  }

  selectTab(clickedItem) {
    if (!clickedItem.selected) {
      this._triggerNavigationChangeEvent({ param: clickedItem.pageViewId, action: NavigateAction.CHANGE_TAB });
    }
  }

  selectBookmark(clickedItem) {
    this._triggerUrlChangeEventAfterConfirmation({ param: clickedItem.url, action: NavigateAction.PUSH_PAGE });
  }

  navigationControlChange(event) {
    this._triggerNavigationChangeEvent({ action: event });
  }

  toggleMoreTabs($event) {
    this.showMoreTabs = !this.showMoreTabs;
    $event.preventDefault();
    $event.stopPropagation();
  }

  toggleMoreBookmarks($event) {
    this.showMoreBookmarks = !this.showMoreBookmarks;
    $event.preventDefault();
    $event.stopPropagation();
  }

  isTabHidden(tabIndex) {
    return tabIndex >= this.visibleTabCount && !this.showMoreTabs;
  }

  isBookmarkHidden(bookmarkIndex) {
    return bookmarkIndex >= this.visibleBookmarkCount && !this.showMoreBookmarks;
  }

  private _triggerUrlChangeEventAfterConfirmation(event) {
    if (this._isSelectedUrlInNewDomain(event.param)) {
      this._notifyLeaveDomain(event);
    } else {
      this._triggerNavigationChangeEvent(event);
    }
  }

  private _isSelectedUrlInNewDomain(url) {
    if (/^(https?:\/\/)/.test(url)) {
      return this._getDomainFromUrl(url) !== this._getDomainFromUrl(this.currentUrl);
    }

    return false;
  }

  private _getDomainFromUrl(url) {
    let result;
    // matches the hostname
    let match = url.match(/^(?:https?:\/\/)?(?:www\.)?([^:\/\n\?\=]+)/im);
    if (match) {
      result = match[1];

      // return the main domain
      match = result.match(/([^\.]+\.[^\.]+)$/);
      if (match) {
        result = match[1];
      }
    }
    return result;
  }

  private _notifyLeaveDomain(event) {
    if (!this.isModalOpen) {
      this.isModalOpen = true;
      const modalOptions = {
        headerText: 'navigationBar.notificationHeader',
        messageBody: 'navigationBar.notificationMessage',
        confirmButton: {
          text: 'navigationBar.confirmButtonText',
          clickEvent: () => this._triggerNavigationChangeEvent(event)
        },
        cancelButton: {
          text: 'navigationBar.cancelButtonText'
        },
        resultFunction: () => this._modalResult(),
        closeable: true
      };
      this._modalService.show(modalOptions);
    }
  }

  private _modalResult() {
    this.isModalOpen = false;
  }

  private _triggerNavigationChangeEvent(event) {
    this.selectionChange.emit(event);
  }
}
